type PromiseFunction = () => Promise<void>;

/**
 * Simple mechanism for building a queue of promises.
 *
 *   const pfq = new PromiseFunctionQueue();
 *   pfq.enqueue(async () => {
 *     await doSomething();
 *   });
 *   pfq.enqueue(async () => {
 *     await doSomethingElse();
 *   });
 *
 * The promise functions will be executed in order, one at a time.
 *
 * pfq.kill() can be used to clear the queue.
 */
export default class PromiseFunctionQueue {
  private items: Array<PromiseFunction> = [];
  private isProcessing = false;

  private async processQueue(): Promise<void> {
    this.isProcessing = true;

    while (this.items.length > 0) {
      const fn = this.items.shift()!;
      await fn();
    }

    // Note that there is a race condition here that we will not address. It's in the
    // tiny window between the last async running and `isProcessing` being set to false.
    // The likelihood of this happening is extremely low.
    this.isProcessing = false;

    // Check if any items were added during the race window.
    // If another enqueue() call already started processing, don't start again.
    if (this.items.length > 0 && !this.isProcessing) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.processQueue();
    }
  }

  enqueue(p: PromiseFunction): void {
    this.items.push(p);

    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  kill(): void {
    // This doesn't cancel any in-flight promises, but it does prevent any remaining queued promise functions from running
    this.items = [];
  }
}
